import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  CalendarDays,
  Check,
  Clock3,
  ClipboardCopy,
  FileAudio,
  FolderOpen,
  Languages,
  Mic,
  NotebookPen,
  PanelRightClose,
  PanelRightOpen,
  Pencil,
  Save,
  Sparkles,
  FileText,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { CAPTURE_MODE_LABELS, meetlyApi, meetlyMeetingTitle, type MeetlyActionItem, type MeetlyContextNote, type MeetlyMeeting, type MeetlyMeetingUpdate } from '../api/meetly';
import { useAccess } from '../contexts/AccessContext';
import MeetlyMeetingEditor from '../pages/Meetly/MeetlyMeetingEditor';
import { formatDurationLabel, getFileDurationSeconds } from '../utils/meetlyAudio';
import { parseSummaryBlocks, type SummaryInline } from '../utils/meetlySummary';
import { parseTranscriptLines } from '../utils/meetlyTranscript';
import ConfirmDialog, { type ConfirmState } from './ConfirmDialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';

interface MeetlyResultWorkspaceProps {
  meeting: MeetlyMeeting;
  onMeetingChanged?: () => void;
}

type ActionPanelSize = 'compact' | 'default' | 'wide';

const createTaskPath = (meetingId: string, actionItemId: string) =>
  `/tasks/create?meetly_meeting_id=${encodeURIComponent(meetingId)}&meetly_action_item_id=${encodeURIComponent(actionItemId)}`;

const formatSuggestedDate = (value: string) => {
  const calendarMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})(?:$|T)/);
  if (!calendarMatch) return value;
  if (value.includes('T') && Number.isNaN(new Date(value).getTime())) return value;

  const [, yearText, monthText, dayText] = calendarMatch;
  const year = Number(yearText);
  const month = Number(monthText);
  const day = Number(dayText);
  const date = new Date(year, month - 1, day);
  if (
    Number.isNaN(date.getTime())
    || date.getFullYear() !== year
    || date.getMonth() !== month - 1
    || date.getDate() !== day
  ) return value;
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const meetingDate = (meeting: MeetlyMeeting) => {
  const parsed = new Date(meeting.submitted_at || meeting.created_at || meeting.completed_at);
  return Number.isNaN(parsed.getTime())
    ? 'Date unavailable'
    : parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

const meetingTime = (meeting: MeetlyMeeting) => {
  const parsed = new Date(meeting.submitted_at || meeting.created_at || meeting.completed_at);
  return Number.isNaN(parsed.getTime())
    ? 'Unavailable'
    : parsed.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
};

const LANGUAGE_LABELS: Record<string, string> = { en: 'English', hi: 'Hindi', ur: 'Urdu' };
const languageLabel = (language: string | null) =>
  language ? LANGUAGE_LABELS[language] || language.toUpperCase() : null;

const ownerFor = (item: MeetlyActionItem) =>
  item.owner?.trim() || item.suggested_assignee?.trim() || 'Not mentioned';

const taskFor = (item: MeetlyActionItem) => item.task?.trim() || item.text.trim();

const dueFor = (item: MeetlyActionItem) =>
  item.due?.trim() || (item.suggested_due_date ? formatSuggestedDate(item.suggested_due_date) : 'Not mentioned');

const timestampFor = (item: MeetlyActionItem) => item.segment_timestamp?.trim() || 'Not mentioned';

const actionCopyText = (item: MeetlyActionItem) =>
  `Owner: ${ownerFor(item)}\nTask: ${taskFor(item)}\nDue: ${dueFor(item)}\nTimestamp: ${timestampFor(item)}`;

const InlineText = ({ spans }: { spans: SummaryInline[] }) => (
  <>
    {spans.map((span, index) =>
      span.bold
        ? <strong key={index} className="font-bold">{span.text}</strong>
        : <React.Fragment key={index}>{span.text}</React.Fragment>,
    )}
  </>
);

const toolbarButtonClass =
  'inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white px-3 text-xs font-semibold text-[var(--gt-text-primary)] transition-colors duration-150 hover:bg-[var(--gt-surface-1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] disabled:cursor-not-allowed disabled:opacity-60';

const ghostButtonClass =
  'inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-[var(--gt-radius-input)] px-3 text-xs font-semibold text-[var(--gt-text-secondary)] transition-colors duration-150 hover:bg-[var(--gt-surface-1)] hover:text-[var(--gt-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] disabled:cursor-not-allowed disabled:opacity-60';

function DetailChip({ icon, children }: { icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-[var(--gt-radius-pill)] bg-[var(--gt-surface-1)] px-2.5 py-1 text-xs font-medium text-[var(--gt-text-secondary)]">
      {icon}
      {children}
    </span>
  );
}

export function MeetlyResultWorkspace({
  meeting,
  onMeetingChanged,
}: MeetlyResultWorkspaceProps) {
  const navigate = useNavigate();
  const { hasAccess, isPanelVisible } = useAccess();
  const canCreateTasks = !hasAccess || isPanelVisible('tasks');
  const importInputRef = useRef<HTMLInputElement>(null);

  const actionItems = useMemo(
    () => [...meeting.action_items].sort((left, right) => left.order_index - right.order_index),
    [meeting.action_items],
  );
  const summaryBlocks = useMemo(() => parseSummaryBlocks(meeting.summary), [meeting.summary]);

  const meetingName = meetlyMeetingTitle(meeting);
  const durationLabel = meeting.duration_seconds != null ? formatDurationLabel(meeting.duration_seconds) : null;
  const sourceLabel = meeting.capture_mode ? CAPTURE_MODE_LABELS[meeting.capture_mode] || meeting.capture_mode : null;

  const [language, setLanguage] = useState(meeting.language || 'en');
  const [template, setTemplate] = useState(meeting.summary_template || 'project_sync');
  const [statusText, setStatusText] = useState<string | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const [contextNotes, setContextNotes] = useState<MeetlyContextNote[]>(meeting.context_notes || []);
  const [isContextOpen, setIsContextOpen] = useState(false);
  const [newContextText, setNewContextText] = useState('');
  const [contextSaving, setContextSaving] = useState(false);
  const [contextError, setContextError] = useState<string | null>(null);
  const [editingNoteId, setEditingNoteId] = useState<string | null>(null);
  const [editingText, setEditingText] = useState('');
  const [savingNoteId, setSavingNoteId] = useState<string | null>(null);
  const [confirmState, setConfirmState] = useState<ConfirmState | null>(null);
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const [transcriptText, setTranscriptText] = useState('');
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState<string | null>(null);
  const [isMeetingEditorOpen, setIsMeetingEditorOpen] = useState(false);
  const [actionPanelSize, setActionPanelSize] = useState<ActionPanelSize>('default');
  const [actionPanelCollapsed, setActionPanelCollapsed] = useState(false);

  useEffect(() => {
    setContextNotes(meeting.context_notes || []);
  }, [meeting.id]);

  const copyText = [
    meeting.summary || '',
    actionItems.length
      ? `\n\nAction Items\n${actionItems.map((item, index) => `${index + 1}. ${actionCopyText(item)}`).join('\n\n')}`
      : '',
  ].join('').trim();

  const copyToClipboard = async (value: string, fallback: string) => {
    try {
      await navigator.clipboard?.writeText(value);
      setStatusText(fallback);
    } catch {
      setStatusText('Copy is unavailable in this browser.');
    }
  };

  const saveSummary = () => {
    const blob = new Blob([copyText || 'No summary available.'], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `meetly-${meeting.id}.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setStatusText('Saved summary.');
  };

  const openTranscript = async () => {
    if (transcriptLoading) return;
    setIsTranscriptOpen(true);
    setTranscriptLoading(true);
    setTranscriptError(null);
    setTranscriptText('');
    try {
      const response = await meetlyApi.getTranscript(meeting.id);
      setTranscriptText(response.data.transcript || '');
    } catch {
      setTranscriptError('The transcript could not be loaded. Please try again.');
    } finally {
      setTranscriptLoading(false);
    }
  };

  const saveTranscript = () => {
    const blob = new Blob([transcriptText], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `meetly-${meeting.id}-transcript.txt`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const uploadCapture = useCallback(async (file: File) => {
    setUploading(true);
    setStatusText(null);
    setErrorText(null);
    try {
      const durationSeconds = await getFileDurationSeconds(file);
      const response = await meetlyApi.createMeeting(file, {
        language,
        summary_template: template,
        duration_seconds: durationSeconds,
        capture_mode: 'imported',
      });
      navigate(`/meetly/meetings/${response.data.meeting_id}`);
    } catch {
      setErrorText('That audio could not be uploaded. Check your connection and try again.');
    } finally {
      setUploading(false);
    }
  }, [language, navigate, template]);

  const handleRegenerateSummary = async () => {
    setIsRegenerating(true);
    setErrorText(null);
    setStatusText('Starting summary regeneration...');
    try {
      await meetlyApi.regenerateSummary(meeting.id, {
        language,
        summary_template: template,
      });
      setStatusText('Summary regeneration started.');
      onMeetingChanged?.();
    } catch {
      setStatusText(null);
      setErrorText('Summary regeneration could not be started. Try again.');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleAddContextNote = async () => {
    const text = newContextText.trim();
    if (!text || contextSaving) return;
    setContextSaving(true);
    setContextError(null);
    try {
      const response = await meetlyApi.addContextNote(meeting.id, text);
      setContextNotes(response.data.meeting.context_notes);
      setNewContextText('');
      onMeetingChanged?.();
    } catch {
      setContextError('That context note could not be saved. Try again.');
    } finally {
      setContextSaving(false);
    }
  };

  const handleSaveContextNote = async (noteId: string) => {
    const text = editingText.trim();
    if (!text || contextSaving) return;
    setContextSaving(true);
    setSavingNoteId(noteId);
    setContextError(null);
    try {
      const response = await meetlyApi.updateContextNote(meeting.id, noteId, text);
      setContextNotes(response.data.meeting.context_notes);
      setEditingNoteId(null);
      onMeetingChanged?.();
    } catch {
      setContextError('That context note could not be updated. Try again.');
    } finally {
      setContextSaving(false);
      setSavingNoteId(null);
    }
  };

  const handleDeleteContextNote = (noteId: string) => {
    setConfirmState({
      title: 'Delete this context note?',
      description: 'It will no longer be used the next time the summary is regenerated.',
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        setContextError(null);
        try {
          const response = await meetlyApi.deleteContextNote(meeting.id, noteId);
          setContextNotes(response.data.meeting.context_notes);
          onMeetingChanged?.();
        } catch (error) {
          setContextError('That context note could not be deleted. Try again.');
          throw error;
        }
      },
    });
  };

  const openTaskCreation = (item: MeetlyActionItem) => {
    setErrorText(null);
    navigate(createTaskPath(meeting.id, item.id));
  };

  const saveMeeting = async (update: MeetlyMeetingUpdate) => {
    await meetlyApi.updateMeeting(meeting.id, update);
    onMeetingChanged?.();
  };

  const deleteMeeting = () => {
    setConfirmState({
      title: 'Delete this meeting?',
      description: `This permanently removes ${meetingName}, its summary, and its action items.`,
      confirmLabel: 'Delete',
      tone: 'danger',
      onConfirm: async () => {
        try {
          await meetlyApi.deleteMeeting(meeting.id);
          navigate('/meetly');
        } catch (error) {
          setErrorText('This meeting could not be deleted. Please try again.');
          throw error;
        }
      },
    });
  };

  return (
    <section
      aria-labelledby="meetly-result-title"
      className="overflow-hidden rounded-[var(--gt-radius-card)] border border-[var(--gt-border)] bg-[var(--gt-surface-0)] shadow-[var(--gt-elev-2)]"
    >
      <input
        ref={importInputRef}
        type="file"
        accept="audio/*,video/*"
        className="hidden"
        aria-label="Import audio file"
        onChange={event => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file) void uploadCapture(file);
        }}
      />
      <header className="flex flex-col gap-3 border-b border-[var(--gt-divider)] bg-[var(--gt-surface-1)] px-6 py-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <Link
            to="/meetly"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[var(--gt-text-tertiary)] transition-colors duration-150 hover:text-[var(--gt-primary)]"
          >
            <ArrowLeft size={13} aria-hidden="true" /> All meetings
          </Link>
          <h1 className="gt-display mt-1.5 truncate text-xl text-[var(--gt-text-primary)] sm:text-2xl" title={meetingName}>
            {meetingName}
          </h1>
          <div className="mt-2.5 flex flex-wrap items-center gap-2">
            {durationLabel && (
              <DetailChip icon={<Clock3 size={12} aria-hidden="true" />}>{durationLabel}</DetailChip>
            )}
            <DetailChip icon={<CalendarDays size={12} aria-hidden="true" />}>{meetingDate(meeting)}</DetailChip>
            <DetailChip icon={<Clock3 size={12} aria-hidden="true" />}>Meeting time: {meetingTime(meeting)}</DetailChip>
            {sourceLabel && (
              <DetailChip icon={<Mic size={12} aria-hidden="true" />}>{sourceLabel}</DetailChip>
            )}
            {languageLabel(meeting.language) && (
              <DetailChip icon={<Languages size={12} aria-hidden="true" />}>{languageLabel(meeting.language)}</DetailChip>
            )}
          </div>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <button type="button" onClick={() => navigate('/meetly?record=1')} className={toolbarButtonClass}>
            <FolderOpen size={14} aria-hidden="true" /> Recording
          </button>
          <button
            type="button"
            onClick={() => importInputRef.current?.click()}
            disabled={uploading}
            className={toolbarButtonClass}
          >
            <FileAudio size={14} aria-hidden="true" /> {uploading ? 'Uploading…' : 'Import Audio'}
          </button>
          <button
            type="button"
            onClick={() => void openTranscript()}
            disabled={transcriptLoading}
            className={toolbarButtonClass}
          >
            <FileText size={14} aria-hidden="true" /> {transcriptLoading ? 'Loading…' : 'Transcript'}
          </button>
          <button type="button" aria-label="Edit meeting" onClick={() => setIsMeetingEditorOpen(true)} className={ghostButtonClass}>
            <Pencil size={14} aria-hidden="true" /> Edit
          </button>
          <button type="button" aria-label="Delete meeting" onClick={deleteMeeting} className="inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-[var(--gt-radius-input)] px-3 text-xs font-semibold text-red-700 transition-colors duration-150 hover:bg-red-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-600">
            <Trash2 size={14} aria-hidden="true" /> Delete
          </button>
        </div>
      </header>

      <div className={`grid min-h-[680px] xl:h-[calc(100vh-14rem)] xl:min-h-[680px] ${actionPanelCollapsed
        ? 'xl:grid-cols-1'
        : actionPanelSize === 'compact'
          ? 'xl:grid-cols-[minmax(0,1fr)_minmax(340px,0.45fr)]'
          : actionPanelSize === 'wide'
            ? 'xl:grid-cols-[minmax(0,0.8fr)_minmax(760px,1.4fr)]'
            : 'xl:grid-cols-[minmax(0,1.2fr)_minmax(480px,0.95fr)]'
      }`}>
        <article className={`flex min-h-[540px] flex-col border-b border-[var(--gt-divider)] xl:min-h-0 xl:border-b-0 ${actionPanelCollapsed ? 'xl:border-r-0' : 'xl:border-r'}`} aria-labelledby="meetly-result-title">
          <div className="flex min-h-[58px] flex-wrap items-center gap-2 border-b border-[var(--gt-divider)] px-4 py-2">
            <button
              type="button"
              onClick={() => void handleRegenerateSummary()}
              disabled={isRegenerating}
              className="inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-[var(--gt-radius-pill)] bg-[var(--gt-primary)] px-4 text-xs font-bold text-white transition-colors duration-150 hover:bg-[var(--gt-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2 disabled:cursor-wait disabled:opacity-60"
            >
              <Sparkles size={14} aria-hidden="true" />
              {isRegenerating ? 'Regenerating…' : 'Regenerate Summary'}
            </button>

            <button
              type="button"
              onClick={() => setIsContextOpen(open => !open)}
              aria-expanded={isContextOpen}
              className={toolbarButtonClass}
            >
              <NotebookPen size={14} aria-hidden="true" /> Add context
              {contextNotes.length > 0 && (
                <span
                  aria-hidden="true"
                  className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[var(--gt-primary)] px-1 text-[10px] font-bold text-white"
                >
                  {contextNotes.length}
                </span>
              )}
            </button>

            {actionPanelCollapsed && (
              <button
                type="button"
                onClick={() => setActionPanelCollapsed(false)}
                className={toolbarButtonClass}
              >
                <Wand2 size={14} aria-hidden="true" /> Show Action Items
              </button>
            )}

            <div className="inline-flex items-center gap-1 rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white px-1">
              <label className="inline-flex min-h-9 items-center gap-1 px-1 text-xs font-semibold text-[var(--gt-text-secondary)]">
                <Languages size={14} aria-hidden="true" />
                <span className="sr-only">Summary language</span>
                <select
                  aria-label="Summary language"
                  value={language}
                  onChange={event => setLanguage(event.target.value)}
                  className="cursor-pointer bg-transparent outline-none"
                >
                  <option value="en">English</option>
                  <option value="hi">Hindi</option>
                  <option value="ur">Urdu</option>
                </select>
              </label>

              <span className="h-4 w-px bg-[var(--gt-divider)]" aria-hidden="true" />

              <label className="inline-flex min-h-9 items-center gap-1 px-1 text-xs font-semibold text-[var(--gt-text-secondary)]">
                <FileText size={14} aria-hidden="true" />
                <span className="sr-only">Summary template</span>
                <select
                  aria-label="Summary template"
                  value={template}
                  onChange={event => setTemplate(event.target.value)}
                  className="cursor-pointer bg-transparent outline-none"
                >
                  <option value="project_sync">Project sync</option>
                  <option value="daily_standup">Daily standup</option>
                  <option value="client_call">Client call</option>
                  <option value="general">General</option>
                </select>
              </label>
            </div>

            <div className="ml-auto flex items-center gap-1">
              <button type="button" onClick={saveSummary} className={ghostButtonClass}>
                <Save size={14} aria-hidden="true" /> Save
              </button>
              <button
                type="button"
                onClick={() => void copyToClipboard(copyText, 'Copied summary.')}
                className={ghostButtonClass}
              >
                <ClipboardCopy size={14} aria-hidden="true" /> Copy
              </button>
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-8 py-7">
            <div className="mb-5">
              <p className="gt-eyebrow">Meeting summary</p>
              <h2 id="meetly-result-title" className="gt-display mt-1 text-2xl text-[var(--gt-text-primary)]">
                Summary
              </h2>
            </div>

            {isContextOpen && (
              <section className="mb-5 rounded-[var(--gt-radius-card)] border border-[var(--gt-accent-soft)] bg-[var(--gt-primary-container)] p-4" aria-labelledby="meetly-context-title">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 id="meetly-context-title" className="text-sm font-bold text-[var(--gt-text-primary)]">Meeting context</h3>
                    <p className="mt-1 text-xs leading-5 text-[var(--gt-text-secondary)]">Add information that should guide the next summary.</p>
                  </div>
                  <button type="button" onClick={() => setIsContextOpen(false)} className={ghostButtonClass}>Close</button>
                </div>

                {contextNotes.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {contextNotes.map(note => (
                      <div key={note.id} className="rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white p-3">
                        {editingNoteId === note.id && savingNoteId !== note.id ? (
                          <div className="space-y-2">
                            <textarea rows={3} value={editingText} onChange={event => setEditingText(event.target.value)} className="w-full resize-y rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white px-2.5 py-2 text-sm leading-6 text-[var(--gt-text-primary)] outline-none focus:border-[var(--gt-primary)]" />
                            <div className="flex justify-end gap-2">
                              <button type="button" onClick={() => setEditingNoteId(null)} className={ghostButtonClass}>Cancel</button>
                              <button type="button" onClick={() => void handleSaveContextNote(note.id)} disabled={!editingText.trim() || contextSaving} className="inline-flex min-h-9 cursor-pointer items-center rounded-[var(--gt-radius-input)] bg-[var(--gt-primary)] px-3 text-xs font-bold text-white transition-colors duration-150 hover:bg-[var(--gt-primary-hover)] disabled:cursor-not-allowed disabled:opacity-60">Save</button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm leading-6 text-[var(--gt-text-primary)]">{note.text}</p>
                            <div className="flex shrink-0 items-center gap-1">
                              <button type="button" aria-label="Edit context note" onClick={() => { setEditingNoteId(note.id); setEditingText(note.text); }} className="rounded p-1.5 text-[var(--gt-text-tertiary)] transition-colors duration-150 hover:bg-[var(--gt-surface-2)] hover:text-[var(--gt-text-primary)]"><Pencil size={13} aria-hidden="true" /></button>
                              <button type="button" aria-label="Delete context note" onClick={() => handleDeleteContextNote(note.id)} className="rounded p-1.5 text-[var(--gt-text-tertiary)] transition-colors duration-150 hover:bg-red-50 hover:text-red-600"><Trash2 size={13} aria-hidden="true" /></button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {contextError && <p role="alert" className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold leading-5 text-red-800">{contextError}</p>}

                <div className="mt-3">
                  <label className="sr-only" htmlFor="meetly-context-new">Add context for AI summary</label>
                  <textarea id="meetly-context-new" rows={3} value={newContextText} onChange={event => setNewContextText(event.target.value)} placeholder="Add context for AI summary. For example people involved, meeting overview, objective etc..." className="w-full resize-y rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-white px-3 py-2 text-sm leading-6 text-[var(--gt-text-primary)] outline-none transition-colors duration-150 placeholder:text-[var(--gt-text-tertiary)] focus:border-[var(--gt-primary)]" />
                  <button type="button" onClick={() => void handleAddContextNote()} disabled={!newContextText.trim() || contextSaving} className="mt-2 inline-flex min-h-9 cursor-pointer items-center justify-center gap-2 rounded-[var(--gt-radius-input)] bg-[var(--gt-primary)] px-4 text-xs font-bold text-white transition-colors duration-150 hover:bg-[var(--gt-primary-hover)] disabled:cursor-not-allowed disabled:opacity-60">{contextSaving ? 'Adding…' : 'Add'}</button>
                </div>
              </section>
            )}

            {errorText && (
              <p role="alert" className="mb-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold leading-5 text-red-800">
                {errorText}
              </p>
            )}
            {statusText && (
              <p role="status" className="mb-4 rounded border border-[var(--gt-border)] bg-[var(--gt-surface-1)] px-3 py-2 text-xs font-semibold text-[var(--gt-text-secondary)]">
                {statusText}
              </p>
            )}

            {summaryBlocks.length ? (
              <div className="space-y-3">
                {summaryBlocks.map((block, index) => {
                  if (block.kind === 'heading') {
                    return block.level === 1 ? (
                      <h3 key={index} className="pt-2 text-lg font-bold text-[var(--gt-text-primary)]">
                        <InlineText spans={block.spans} />
                      </h3>
                    ) : (
                      <h4 key={index} className="pt-2 text-base font-bold text-[var(--gt-text-primary)]">
                        <InlineText spans={block.spans} />
                      </h4>
                    );
                  }

                  if (block.kind === 'bullets') {
                    return (
                      <ul key={index} className="list-disc space-y-1.5 pl-5">
                        {block.items.map((item, itemIndex) => (
                          <li key={itemIndex} className="text-[15px] leading-7 text-[var(--gt-text-primary)]">
                            <InlineText spans={item} />
                          </li>
                        ))}
                      </ul>
                    );
                  }

                  return (
                    <p key={index} className="text-[15px] leading-7 text-[var(--gt-text-primary)]">
                      <InlineText spans={block.spans} />
                    </p>
                  );
                })}
              </div>
            ) : (
              <div className="rounded border border-dashed border-[var(--gt-border-strong)] bg-[var(--gt-surface-1)] px-5 py-6">
                <p className="text-sm font-semibold text-[var(--gt-text-primary)]">No summary was generated.</p>
                <p className="mt-1 text-xs leading-5 text-[var(--gt-text-tertiary)]">
                  You can still review any action items identified for this meeting.
                </p>
              </div>
            )}

          </div>
        </article>

        {!actionPanelCollapsed && <aside className="flex min-h-[420px] flex-col bg-[var(--gt-surface-1)] xl:min-h-0" aria-labelledby="meetly-actions-title" data-action-panel-size={actionPanelSize}>
          <div className="flex min-h-[58px] items-center justify-between border-b border-[var(--gt-divider)] px-5 py-3">
            <div className="flex items-center gap-2">
              <Wand2 size={17} className="text-[var(--gt-primary)]" aria-hidden="true" />
              <h2 id="meetly-actions-title" className="text-base font-bold text-[var(--gt-text-primary)]">Action Items</h2>
            </div>
            <div className="flex items-center gap-1.5">
              {actionItems.length > 0 && <span className="rounded-[var(--gt-radius-pill)] bg-[var(--gt-primary)] px-2 py-0.5 text-xs font-bold text-white">{actionItems.length}</span>}
              <button type="button" aria-label="Make Action Items smaller" aria-pressed={actionPanelSize === 'compact'} onClick={() => setActionPanelSize('compact')} title="Make smaller" className="rounded p-1.5 text-[var(--gt-text-tertiary)] transition-colors hover:bg-white hover:text-[var(--gt-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)]"><PanelRightClose size={16} aria-hidden="true" /></button>
              <button type="button" aria-label="Make Action Items larger" aria-pressed={actionPanelSize === 'wide'} onClick={() => setActionPanelSize('wide')} title="Make larger" className="rounded p-1.5 text-[var(--gt-text-tertiary)] transition-colors hover:bg-white hover:text-[var(--gt-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)]"><PanelRightOpen size={16} aria-hidden="true" /></button>
              <button type="button" aria-label="Hide Action Items" onClick={() => setActionPanelCollapsed(true)} title="Hide Action Items" className="rounded p-1.5 text-[var(--gt-text-tertiary)] transition-colors hover:bg-white hover:text-[var(--gt-text-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)]"><X size={16} aria-hidden="true" /></button>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
            <section aria-labelledby="meetly-actions-title" className="flex min-h-full flex-col">
              <div className="flex items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--gt-text-tertiary)]">Tasks generated from this meeting</p>
              </div>

              {actionItems.length ? (
                <div className="mt-3 flex min-h-[560px] flex-1 overflow-hidden rounded-[var(--gt-radius-card)] border border-[var(--gt-border)]">
                  <div className="h-full flex-1 overflow-x-auto">
                    {/* Keep four action columns readable in the narrow panel.
                        The wrapper scrolls horizontally below this width rather
                        than squeezing text into neighbouring columns. */}
                    <table className="min-h-full w-full min-w-[760px] table-fixed border-collapse bg-white text-left text-sm">
                      <thead className="bg-[var(--gt-surface-1)]">
                        <tr>
                          <th scope="col" className="w-[19%] border-b border-[var(--gt-border)] px-4 py-3 font-semibold text-[var(--gt-text-secondary)]">Owner</th>
                          <th scope="col" className="w-[39%] border-b border-[var(--gt-border)] px-4 py-3 font-semibold text-[var(--gt-text-secondary)]">Task</th>
                          <th scope="col" className="w-[17%] border-b border-[var(--gt-border)] px-4 py-3 font-semibold text-[var(--gt-text-secondary)]">Due</th>
                          <th scope="col" className="w-[25%] border-b border-[var(--gt-border)] px-4 py-3 font-semibold text-[var(--gt-text-secondary)]">TeamLio</th>
                        </tr>
                      </thead>
                      <tbody>
                        {actionItems.map(item => {
                          const taskWasCreated = item.task_created || Boolean(item.created_task_id);
                          const hasDueDate = Boolean(item.due?.trim() || item.suggested_due_date);
                          return (
                            <tr key={item.id} className="border-t border-[var(--gt-divider)] align-top">
                              <td className="px-4 py-3.5">
                                <span className="inline-flex max-w-full items-center whitespace-normal break-words rounded-[var(--gt-radius-pill)] bg-[var(--gt-surface-2)] px-2.5 py-1 text-center text-xs font-semibold text-[var(--gt-text-secondary)]">
                                  {ownerFor(item)}
                                </span>
                              </td>
                              <td className="px-4 py-3.5">
                                <span className="block break-words font-semibold leading-6 text-[var(--gt-text-primary)]">
                                  {taskFor(item)}
                                </span>
                              </td>
                              <td className="px-4 py-3.5">
                                {hasDueDate ? (
                                  <span className="inline-flex items-center rounded-[var(--gt-radius-pill)] bg-[var(--gt-primary-container)] px-2.5 py-1 text-xs font-semibold text-[var(--gt-primary-deep)]">
                                    {dueFor(item)}
                                  </span>
                                ) : (
                                  <span className="block break-words text-xs text-[var(--gt-text-secondary)]">{dueFor(item)}</span>
                                )}
                                <span className="mt-1 block font-mono text-xs tabular-nums text-[var(--gt-text-secondary)]">
                                  {timestampFor(item)}
                                </span>
                              </td>
                              <td className="px-4 py-3.5">
                                {taskWasCreated && item.created_task_id && canCreateTasks ? (
                                  <Link
                                    to={`/tasks/${encodeURIComponent(item.created_task_id)}`}
                                    className="inline-flex min-h-9 max-w-full flex-wrap items-center justify-center gap-1.5 whitespace-normal rounded-[var(--gt-radius-input)] border border-emerald-200 bg-emerald-50 px-3 text-center text-xs font-bold text-emerald-800 transition-colors duration-150 hover:bg-emerald-100"
                                  >
                                    <Check size={13} aria-hidden="true" /> Task created <span className="font-mono">{item.created_task_id}</span>
                                  </Link>
                                ) : taskWasCreated ? (
                                  <span role="status" className="inline-flex min-h-9 max-w-full flex-wrap items-center justify-center gap-1.5 whitespace-normal rounded-[var(--gt-radius-input)] border border-emerald-200 bg-emerald-50 px-3 text-center text-xs font-bold text-emerald-800">
                                    <Check size={13} aria-hidden="true" /> Task already created
                                  </span>
                                ) : canCreateTasks ? (
                                  <button
                                    type="button"
                                    aria-label={`Create task: ${taskFor(item)}`}
                                    onClick={() => openTaskCreation(item)}
                                    className="inline-flex min-h-9 max-w-full cursor-pointer flex-wrap items-center justify-center gap-1.5 whitespace-normal rounded-[var(--gt-radius-input)] bg-[var(--gt-primary)] px-3 text-center text-xs font-bold text-white transition-colors duration-150 hover:bg-[var(--gt-primary-hover)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--gt-primary)] focus-visible:ring-offset-2"
                                  >
                                    Create Task <ArrowUpRight size={13} aria-hidden="true" />
                                  </button>
                                ) : (
                                  <span role="status" className="inline-flex min-h-9 max-w-full flex-wrap items-center justify-center whitespace-normal rounded-[var(--gt-radius-input)] border border-[var(--gt-border)] bg-[var(--gt-surface-1)] px-3 text-center text-xs font-semibold text-[var(--gt-text-tertiary)]">
                                    Task creation unavailable
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="mt-3 rounded-[var(--gt-radius-card)] border border-dashed border-[var(--gt-border-strong)] bg-[var(--gt-surface-1)] px-5 py-7 text-center">
                  <p className="text-sm font-semibold text-[var(--gt-text-primary)]">No action items were identified.</p>
                  <p className="mt-1 text-xs leading-5 text-[var(--gt-text-tertiary)]">
                    This meeting is complete and needs no task follow-up.
                  </p>
                </div>
              )}
            </section>
          </div>
        </aside>}
      </div>

      <Sheet open={isTranscriptOpen} onOpenChange={setIsTranscriptOpen}>
        <SheetContent side="right" className="flex w-full flex-col gap-0 sm:max-w-xl">
          <SheetHeader>
            <SheetTitle>Transcript</SheetTitle>
          </SheetHeader>

          <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
            {transcriptLoading ? (
              <div role="status" aria-label="Loading transcript" className="space-y-3">
                {[0, 1, 2, 3].map(index => (
                  <div key={index} className="h-3 animate-pulse rounded bg-[var(--gt-border)] motion-reduce:animate-none" style={{ width: `${92 - index * 13}%` }} />
                ))}
              </div>
            ) : transcriptError ? (
              <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
                <p role="alert">{transcriptError}</p>
                <button type="button" onClick={() => void openTranscript()} className="mt-3 rounded-[var(--gt-radius-input)] border border-red-200 bg-white px-3 py-1.5 text-xs font-bold text-red-800 hover:bg-red-100">Try again</button>
              </div>
            ) : transcriptText ? (
              <ol className="space-y-4">
                {parseTranscriptLines(transcriptText).map((line, index) => (
                  <li key={`${line.timestamp || 'line'}-${index}`} className="grid grid-cols-[58px_minmax(0,1fr)] gap-3">
                    <time className="pt-1 font-mono text-[11px] tabular-nums text-[var(--gt-text-tertiary)]">{line.timestamp ? `[${line.timestamp}]` : ''}</time>
                    <p className="text-sm leading-6 text-[var(--gt-text-primary)]">{line.text}</p>
                  </li>
                ))}
              </ol>
            ) : (
              <p className="text-sm leading-6 text-[var(--gt-text-tertiary)]">No transcript is available for this meeting.</p>
            )}
          </div>

          {transcriptText && !transcriptLoading && (
            <div className="mt-4 flex shrink-0 justify-end gap-2 border-t border-[var(--gt-divider)] pt-4">
              <button type="button" onClick={() => void copyToClipboard(transcriptText, 'Copied transcript.')} className={ghostButtonClass}>
                <ClipboardCopy size={14} aria-hidden="true" /> Copy
              </button>
              <button type="button" onClick={saveTranscript} className={ghostButtonClass}>
                <Save size={14} aria-hidden="true" /> Save
              </button>
            </div>
          )}
        </SheetContent>
      </Sheet>

      {isMeetingEditorOpen && (
        <MeetlyMeetingEditor
          meeting={meeting}
          onClose={() => setIsMeetingEditorOpen(false)}
          onSave={saveMeeting}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </section>
  );
}

export default MeetlyResultWorkspace;
